import { defineStore } from "pinia"
import { ref, computed } from "vue"
import Cookies from "js-cookie"
import router from "@/router/index"
import MetaService from "@/services/resources/MetaService"
import MemberService from "@/services/resources/MemberService"
import { play } from "@voomp/play-back"
import { paths } from "@/utils/route-paths"
import { tryJsonParse } from "@/utils/json-parser"

/* Services */
const serviceMeta = MetaService.build()
const serviceMember = MemberService.build()

/* Cookie deletion utility */
const delete_cookie = (name: string) => {
  document.cookie = name + "=;expires=Thu, 01 Jan 1970 00:00:01 GMT;"
  document.cookie =
    name +
    `=;path=/; domain=.voompplay.${import.meta.env.VUE_URL_DOMAIN};expires=Thu, 01 Jan 1970 00:00:01 GMT;`
  document.cookie =
    name +
    `=;path=/; domain=voompplay.${import.meta.env.VUE_URL_DOMAIN};expires=Thu, 01 Jan 1970 00:00:01 GMT;`
  document.cookie =
    name +
    "=;path=/; domain=." +
    window.location.host +
    ";expires=Thu, 01 Jan 1970 00:00:01 GMT;"
  document.cookie =
    name +
    "=;path=/; domain=" +
    window.location.host +
    ";expires=Thu, 01 Jan 1970 00:00:01 GMT;"
}

export interface CurrentSiteData {
  currentSite?: {
    name?: string
    watch_v3?: boolean
    [key: string]: unknown
  }
  [key: string]: unknown
}

export interface UserMetas {
  [key: string]: unknown
}

export const useUserStore = defineStore('user', () => {
  /* State — initialized from cookies where applicable */
  const metasSite = ref<Record<string, unknown>>(
    tryJsonParse<Record<string, unknown>>(Cookies.get("metasSite")) || {}
  )
  const certificateConfig = ref<Record<string, unknown>>(
    tryJsonParse<Record<string, unknown>>(Cookies.get("certificateConfig")) || {}
  )
  const existConfigCert = ref<boolean>(false)
  const currentSite = ref<CurrentSiteData>(
    tryJsonParse<CurrentSiteData>(Cookies.get("currentSite")) || {}
  )
  const metas = ref<UserMetas>({})

  /* Getters (computed) */
  const getMetasSite = computed(() => metasSite.value)
  const getCertificateConfig = computed(() => certificateConfig.value)
  const getCurrentSite = computed(() => currentSite.value)
  const getExistConfigCert = computed(() => existConfigCert.value)
  const getMetas = computed(() => metas.value)

  /* Mutations → Actions */
  function setMetasSite(resp: Record<string, unknown>) {
    metasSite.value = resp
    Cookies.set("metasSite", JSON.stringify(resp), { expires: 365 })
  }

  function setExistConfigCert(resp: boolean) {
    existConfigCert.value = resp
  }

  function setCertificateConfig(resp: Record<string, unknown>) {
    certificateConfig.value = resp
    Cookies.set("certificateConfig", JSON.stringify(resp || {}), { expires: 365 })
  }

  function setCurrentSite(resp: CurrentSiteData) {
    currentSite.value = resp
    paths.toggles.watch = resp?.currentSite?.watch_v3 ? '3' : '1'
    document.title = (resp?.currentSite?.name || "") + " - Voomp Play"
    Cookies.set("currentSite", JSON.stringify(resp), { expires: 365 })
  }

  function loginSuccess(resp: unknown) {
    Cookies.set("sites", JSON.stringify(resp), { expires: 365 })
  }

  function userLogout() {
    delete_cookie("sites")
    delete_cookie("userCustomFields")
    delete_cookie("memberAuth")
    delete_cookie("access_level")
    delete_cookie("auth_greennCourse")
    delete_cookie("blogsClub")
    sessionStorage.clear()
    localStorage.clear()
    router.replace("/")
  }

  function setMetas(payload: UserMetas) {
    metas.value = payload
  }

  /* Actions */
  async function getMetasSiteAction() {
    const resp = await serviceMeta.search(
      "keys[]=study_schedule_allowed&keys[]=exercises_allowed&keys[]=onboarding&keys[]=comment_review&keys[]=helper_data&keys[]=main_color&keys[]=logo&keys[]=theme_fixed_painel&keys[]=theme_fixed_painel_color&keys[]=termsOfUseActivated&keys[]=logomarca"
    )
    setMetasSite(resp)
    return resp
  }

  function getCurrentSiteAndMember() {
    // empty — kept for backward compatibility
  }

  function userRequest() {
    redirectUserToHome()
  }

  function redirectUserToHome() {
    router.go(0)
  }

  async function actionFetchMetas({ keys }: { keys: string[] }) {
    const basePath = ["meta"].join("/")
    const keysPath = keys.map((key) => `keys[]=${key}`).join("&")
    const path = [basePath, keysPath].join("?")
    const result = await serviceMember.read(path)
    return result || {}
  }

  async function actionSaveMeta({ key, value }: { key: string; value: unknown }) {
    const data = {
      id: `/meta/${key}`,
      value,
    }
    await serviceMember.postID(data)
    metas.value = {
      ...metas.value,
      [key]: value,
    }
  }

  /** Alias for actionSaveMeta — referenced as "user/actionSaveSiteMetas" in some components */
  async function actionSaveSiteMetas({ key, value }: { key: string; value: unknown }) {
    return actionSaveMeta({ key, value })
  }

  function actionManualSave(data: unknown) {
    return play.member.register_manual(data)
  }

  async function actionGetPurchases() {
    return await serviceMember.read("purchases")
  }

  async function actionCancelSubscription({ purchaseId }: { purchaseId: string | number }) {
    return await serviceMember.delete(`purchases/${purchaseId}`)
  }

  function $reset() {
    metasSite.value = {}
    certificateConfig.value = {}
    existConfigCert.value = false
    currentSite.value = {}
    metas.value = {}
  }

  return {
    /* State */
    metasSite,
    certificateConfig,
    existConfigCert,
    currentSite,
    metas,

    /* Getters */
    getMetasSite,
    getCertificateConfig,
    getCurrentSite,
    getExistConfigCert,
    getMetas,

    /* Mutations (as functions) */
    setMetasSite,
    setExistConfigCert,
    setCertificateConfig,
    setCurrentSite,
    loginSuccess,
    userLogout,
    setMetas,

    /* Actions */
    getMetasSiteAction,
    getCurrentSiteAndMember,
    userRequest,
    redirectUserToHome,
    actionFetchMetas,
    actionSaveMeta,
    actionSaveSiteMetas,
    actionManualSave,
    actionGetPurchases,
    actionCancelSubscription,
    $reset,
  }
})
